/**
 * Create Audit Session Dialog
 *
 * Modal dialog for creating new audit sessions with basic information
 * and content filters. Refactored to use sub-components for better maintainability.
 */

import type { SampleFilters } from '@internal-audit/domain';
import { useAuditSessionFilterOptions } from '@internal-audit/hooks/useAuditSessionFilterOptions';
import { useCreateAuditSession } from '@internal-audit/hooks/useCreateAuditSession';
import { Dialog, LoadingSpinner, Text } from '@soteria/design-system-ve';
import { useCallback, useState, useEffect } from 'react';

import * as styles from './CreateAuditSessionDialog.css';
import { ContentFiltersForm } from './components/ContentFiltersForm';
import { SessionDetailsForm } from './components/SessionDetailsForm';
import { resetFormState, createSampleParameters } from './utils/FormStateManager';
import { validateDateRange, getDefaultStartDate, getDefaultEndDate, getInitialFilters } from './utils/ValidationHelpers';

interface CreateAuditSessionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  communityId: string;
  communityName: string;
  discoveredCustomValues: Record<string, string[]>;
  discoveryLoading: boolean;
  onSessionCreated?: () => void; // Optional callback for when session is successfully created
}

export function CreateAuditSessionDialog({
  isOpen,
  onClose,
  communityId,
  communityName,
  discoveredCustomValues,
  discoveryLoading,
  onSessionCreated,
}: CreateAuditSessionDialogProps) {
  const { createAuditSession, loading: mutationLoading } = useCreateAuditSession();
  const { filterOptions, loading: filterOptionsLoading } = useAuditSessionFilterOptions(communityId);

  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [filters, setFilters] = useState<SampleFilters>(getInitialFilters());
  const [error, setError] = useState('');
  const [dateError, setDateError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const totalLoading = isLoading || mutationLoading || filterOptionsLoading;

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      resetFormState({
        setSessionName,
        setSessionDescription,
        setStartDate,
        setEndDate,
        setFilters,
        setError,
        setDateError,
      });
    }
  }, [isOpen]);

  const handleStartDateChange = useCallback(
    (value: string) => {
      setStartDate(value);
      const error = validateDateRange(value, endDate);

      setDateError(error);
    },
    [endDate]
  );

  const handleEndDateChange = useCallback(
    (value: string) => {
      setEndDate(value);
      const error = validateDateRange(startDate, value);

      setDateError(error);
    },
    [startDate]
  );

  // Helper function to handle successful session creation
  const handleSessionSuccess = useCallback(() => {
    onSessionCreated?.();
    onClose();
  }, [onSessionCreated, onClose]);

  // Helper function to handle session creation errors
  const handleSessionError = useCallback((result?: { errors?: Array<{ userMessage?: string }> }) => {
    const firstError = result?.errors?.[0];

    setError(firstError?.userMessage || 'Failed to create audit session. Please try again.');
  }, []);

  const handleCreateSession = useCallback(async () => {
    if (!sessionName.trim() || !startDate || !endDate) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await createAuditSession({
        input: {
          name: sessionName.trim(),
          description: sessionDescription.trim() || undefined,
          sampleParameters: createSampleParameters({
            communityId,
            startDate,
            endDate,
            filters,
          }),
        },
      });

      if (result.success) {
        handleSessionSuccess();
      } else {
        handleSessionError(result);
      }
    } catch {
      handleSessionError();
    } finally {
      setIsLoading(false);
    }
  }, [sessionName, startDate, endDate, createAuditSession, sessionDescription, communityId, filters, handleSessionSuccess, handleSessionError]);

  const isFormValid = sessionName.trim() && startDate && endDate && !dateError;

  return (
    <Dialog
      description="Configure a new audit session with content filters for the selected community."
      isOpen={isOpen}
      primaryAction={{
        label: totalLoading ? <LoadingSpinner /> : 'Create Audit Session',
        onClick: handleCreateSession,
        disabled: totalLoading || !isFormValid,
        variant: 'primary',
      }}
      secondaryAction={{
        label: 'Cancel',
        onClick: onClose,
        variant: 'secondary',
      }}
      size="xl"
      title="Create New Audit Session"
      onClose={onClose}
    >
      <div className={styles.dialogContent}>
        {error && (
          <div className={styles.errorContainer}>
            <Text className={styles.errorText}>{error}</Text>
          </div>
        )}

        <SessionDetailsForm
          communityName={communityName}
          dateError={dateError}
          endDate={endDate}
          sessionDescription={sessionDescription}
          sessionName={sessionName}
          startDate={startDate}
          totalLoading={totalLoading}
          onEndDateChange={handleEndDateChange}
          onSessionDescriptionChange={setSessionDescription}
          onSessionNameChange={setSessionName}
          onStartDateChange={handleStartDateChange}
        />

        <ContentFiltersForm
          discoveredCustomValues={discoveredCustomValues}
          discoveryLoading={discoveryLoading}
          filterOptions={filterOptions || null}
          filterOptionsLoading={filterOptionsLoading}
          filters={filters}
          totalLoading={totalLoading}
          onFiltersChange={setFilters}
        />
      </div>
    </Dialog>
  );
}
